import { flag } from '@/lib/feedback';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import qrcode from 'qrcode-generator';
import { ShieldCheck, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, Card } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { toast } from '@/lib/toast';

/** Two-step sign-in (authenticator app) for the signed-in person: set up, confirm, or turn off. */
export function TwoStepCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['auth', '2fa'], queryFn: () => api<{ enabled: boolean }>('/auth/2fa') });
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true); setErr(null);
    try { await fn(); toast.success(done); setSetup(null); setCode(''); setPassword(''); await qc.invalidateQueries({ queryKey: ['auth', '2fa'] }); }
    catch (e) { flag(setErr, errorMessage(e)); }
    finally { setBusy(false); }
  };
  const qr = (uri: string) => { const q = qrcode(0, 'M'); q.addData(uri); q.make(); return q.createDataURL(5, 2); };

  return (
    <Card className="w-full max-w-md p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-full bg-primary-soft text-primary"><ShieldCheck className="size-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">Two-step sign-in</h2>
          <p className="text-[13px] text-fg-muted">After your password, also ask for a code from your phone.</p>
        </div>
        {data && <Badge tone={data.enabled ? 'success' : 'neutral'}>{data.enabled ? 'On' : 'Off'}</Badge>}
      </div>

      {data?.enabled ? (
        <div className="flex flex-col gap-3">
          <p className="text-[13px] text-fg-muted">To turn it off, confirm your password. If you lose your phone, ask the administrator to reset your password — that also resets two-step sign-in.</p>
          <Input type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {err && <p className="text-[13px] text-danger">{err}</p>}
          <div className="flex justify-end"><Button variant="secondary" disabled={busy || !password} onClick={() => void run(() => api('/auth/2fa/disable', { method: 'POST', body: { password } }), 'Two-step sign-in turned off')}>Turn off</Button></div>
        </div>
      ) : setup ? (
        <div className="flex flex-col gap-3">
          <ol className="list-decimal pl-5 text-[13px] text-fg-muted">
            <li>Install Google Authenticator or Microsoft Authenticator on your phone.</li>
            <li>In the app, add an account and scan this code.</li>
            <li>Type the 6-digit code the app shows.</li>
          </ol>
          <div className="flex items-center gap-4">
            <img src={qr(setup.uri)} alt="Scan with your authenticator app" className="size-36 rounded-lg border border-border bg-white p-1" />
            <div className="min-w-0 text-[12px] text-fg-subtle">Can’t scan? Enter this key:<code className="mt-1 block font-mono text-[12.5px] break-all text-fg select-all">{setup.secret.match(/.{1,4}/g)?.join(' ')}</code></div>
          </div>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123 456" value={code} onChange={(e) => setCode(e.target.value)} className="text-[16px] tracking-[0.3em] tabular-nums" />
          {err && <p className="text-[13px] text-danger">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSetup(null)}>Cancel</Button>
            <Button disabled={busy || code.replace(/\s/g, '').length !== 6} onClick={() => void run(() => api('/auth/2fa/enable', { method: 'POST', body: { code } }), 'Two-step sign-in is on')}>Confirm & turn on</Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button disabled={busy || !data} onClick={async () => { setBusy(true); setErr(null); try { setSetup(await api<{ secret: string; uri: string }>('/auth/2fa/setup', { method: 'POST' })); } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); } }}>
            <Smartphone /> Set up with my phone
          </Button>
        </div>
      )}
    </Card>
  );
}
